import { UserRepository } from '../internal/repositories/UserRepository';
import { SignUpType } from '../constants/SignUpTypeEnum';
import { diContainer, serviceNames } from '../service-setup/diContainer';
import { SignUpUserStatusKey } from '../api/RampPgApiSdk';
import { RampStore } from '../internal/stores/RampStore';
import { UserType } from '../constants/UserTypeEnum';
import {
    completeSignUpGenericErrorMessage,
    partialSignUpGenericErrorMessage,
    completeUserExistsErrorMessage,
    partialUserExistsErrorMessage
} from '../constants/Constants';
import { toJS } from 'mobx';
import { LoginSubmittedUseCase } from './LoginSubmittedUseCase';
import { SignUpOrigin } from '../constants/SignUpOriginEnum';

export class SignUpSubmittedUseCase {
    private userRepository: UserRepository;
    private type: SignUpType;
    private origin: SignUpOrigin;
    private rampStore: RampStore;

    constructor(type: SignUpType, origin: SignUpOrigin = SignUpOrigin.SIGNUP_FORM) {
        this.type = type;
        this.origin = origin;
        this.rampStore = diContainer.get(serviceNames.rampStore);
        this.userRepository = diContainer.get(serviceNames.userRepository);
    }

    // Populate onlineSignUpFormStore, which used by Thank You page, with userFormStore values.
    public prePopulateThankYouPage = () => {
        const {
            userFormStore: { values },
            onlineSignUpFormStore
        } = this.userRepository;
        Object.keys(values).forEach((field) => {
            onlineSignUpFormStore.setFieldValue(field, values[field]);
        });
    };

    // When new partial offline user created or partial online user updated via RAMP form
    private onPartialSignUpSuccess = (userId: number, posFrontiersErrorKey?: string) => {
        if (posFrontiersErrorKey) {
            this.userRepository.setPosFrontierError({ transKey: posFrontiersErrorKey });
        }
        this.userRepository.setUserId(userId);
        // show complete sign up page
        this.prePopulateThankYouPage();
        this.rampStore.goToThankYouPage();
        this.userRepository.resetOverlay();
        this.userRepository.userFormStore.clearStore();
    };

    private setOverlayState = (userType: UserType, origin: SignUpOrigin) => {
        const { setUserType, setErrorMessages } = this.userRepository;
        setUserType(userType);
        if (origin === SignUpOrigin.OVERLAY) {
            setErrorMessages([
                {
                    transKey:
                        userType === UserType.PARTIAL
                            ? partialUserExistsErrorMessage
                            : completeUserExistsErrorMessage
                }
            ]);
        }
    };

    public execute = async () => {
        const {
            clearErrorMessages,
            setPosFrontierError,
            signUp,
            updateUser,
            completeSignUp,
            setErrorMessages,
            userId: existingUserId,
            userFormStore,
            onlineSignUpFormStore
        } = this.userRepository;

        clearErrorMessages();
        setPosFrontierError(null);

        if (this.type === SignUpType.RAMP) {
            const formValues = toJS(userFormStore.values);
            const { userId, userStatusKey, posFrontiersErrorKey } = await signUp(formValues);
            switch (userStatusKey) {
                case SignUpUserStatusKey.EXISTING_PARTIAL:
                    // Directly send a request to update user profile
                    const {
                        userStatusKey: updateStatusKey,
                        posFrontiersErrorKey: updatePosErrorKey
                    } = await updateUser(userId, formValues);
                    if (updateStatusKey === SignUpUserStatusKey.EXISTING_PARTIAL_OFFLINE) {
                        this.onPartialSignUpSuccess(userId, updatePosErrorKey);
                    } else {
                        setErrorMessages([{ transKey: partialSignUpGenericErrorMessage }]);
                    }
                    break;
                case SignUpUserStatusKey.EXISTING_PARTIAL_OFFLINE:
                    this.setOverlayState(UserType.PARTIAL, this.origin);
                    break;
                case SignUpUserStatusKey.EXISTING_COMPLETE:
                case SignUpUserStatusKey.EXISTING_COMPLETE_OFFLINE:
                    this.setOverlayState(UserType.COMPLETE, this.origin);
                    break;
                case SignUpUserStatusKey.NEW_PARTIAL_OFFLINE:
                    this.onPartialSignUpSuccess(userId, posFrontiersErrorKey);
                    break;
                default:
                    setErrorMessages([{ transKey: partialSignUpGenericErrorMessage }]);
            }
        } else {
            try {
                const { password, pcEmailAddress } = onlineSignUpFormStore.values;
                if (await completeSignUp(existingUserId, password)) {
                    const useCase = new LoginSubmittedUseCase({ email: pcEmailAddress, password });
                    await useCase.execute();
                } else {
                    setErrorMessages([{ transKey: completeSignUpGenericErrorMessage }]);
                }
            } catch (_) {
                setErrorMessages([{ transKey: completeSignUpGenericErrorMessage }]);
            }
        }
    };
}
